import { useEffect } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import { PRODUCT_DISPLAY_NAME } from '@terra/shared-utils';

type MaintenanceAction =
  | 'reinstall-browser'
  | 'clear-profiles'
  | 'factory-reset';

/**
 * Hook that listens for maintenance actions from the app menu
 * and executes them with appropriate feedback.
 */
export function useMaintenanceActions() {
  useEffect(() => {
    if (!window.electronAPI) return;

    const handleMaintenanceAction = async (...args: unknown[]) => {
      const { action } = args[0] as { action: MaintenanceAction };

      switch (action) {
        case 'reinstall-browser':
          await handleReinstallBrowser();
          break;
        case 'clear-profiles':
          await handleClearProfiles();
          break;
        case 'factory-reset':
          await handleFactoryReset();
          break;
      }
    };

    window.electronAPI.on('maintenance-action', handleMaintenanceAction);

    return () => {
      window.electronAPI?.removeListener(
        'maintenance-action',
        handleMaintenanceAction,
      );
    };
  }, []);
}

async function handleReinstallBrowser() {
  const toastId = 'reinstall-browser';

  toasterApi.loading({
    id: toastId,
    title: 'Reinstalling Browser',
    description: 'Clearing browser installation and downloading fresh copy...',
  });

  try {
    const result = await window.electronAPI?.app.factoryReset({
      clearChromium: true,
      clearProfiles: false,
      clearExtensions: false,
    });

    if (result?.success) {
      toasterApi.success({
        id: toastId,
        title: 'Browser Reinstalled',
        description: 'Browser component has been reinstalled successfully.',
      });
    } else {
      toasterApi.error({
        id: toastId,
        title: 'Reinstall Failed',
        description: result?.error || 'Failed to reinstall browser.',
      });
    }
  } catch (error) {
    toasterApi.error({
      id: toastId,
      title: 'Reinstall Failed',
      description:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  }
}

async function handleClearProfiles() {
  const toastId = 'clear-profiles';
  toasterApi.loading({
    id: toastId,
    title: 'Clearing Profiles',
    description: 'Removing browser profiles and cookies...',
  });

  try {
    const result = await window.electronAPI?.app.factoryReset({
      clearChromium: false,
      clearProfiles: true,
      clearExtensions: false,
    });

    if (result?.success) {
      toasterApi.success({
        id: toastId,
        title: 'Profiles Cleared',
        description:
          'All browser profiles have been cleared. Cookies and localStorage have been removed.',
      });
    } else {
      toasterApi.error({
        id: toastId,
        title: 'Clear Failed',
        description: result?.error || 'Failed to clear profiles.',
      });
    }
  } catch (error) {
    toasterApi.error({
      id: toastId,
      title: 'Clear Failed',
      description:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  }
}

async function handleFactoryReset() {
  const toastId = 'factory-reset';
  toasterApi.loading({
    id: toastId,
    title: 'Factory Reset',
    description: `Resetting ${PRODUCT_DISPLAY_NAME} to factory defaults...`,
  });

  try {
    const result = await window.electronAPI?.app.factoryReset({
      clearChromium: true,
      clearProfiles: true,
      clearExtensions: true,
    });

    if (result?.success) {
      toasterApi.success({
        id: toastId,
        title: 'Factory Reset Complete',
        description: `${PRODUCT_DISPLAY_NAME} has been reset to factory defaults. Browser component has been reinstalled.`,
      });
    } else {
      toasterApi.error({
        id: toastId,
        title: 'Reset Failed',
        description: result?.error || 'Failed to complete factory reset.',
      });
    }
  } catch (error) {
    toasterApi.error({
      id: toastId,
      title: 'Reset Failed',
      description:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    });
  }
}
